// src/context/AuthContext.js
import React, { createContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { login as apiLogin, logout as apiLogout, getKullaniciBilgileri } from "../services/api";
import { getDeviceId } from "../utils/deviceId";

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [token, setToken] = useState(() => localStorage.getItem("token"));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // cihazTokenId = device uuid, sabit kalsın
  const [cihazTokenId] = useState(() => getDeviceId());

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }

      try {
        const res = await getKullaniciBilgileri();

        // normalize (API bazen {data: [...]}, bazen {data: {...}}, bazen direkt {...})
        const normalized =
          res?.data?.data?.[0] ??
          res?.data?.data ??
          res?.data ??
          null;

        setUser(normalized);
} catch (e) {
  const status = e?.response?.status;

  // sadece auth hatasıysa logout gibi davran
  if (status === 401 || status === 403) {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  } else {
    // başka hata: kullanıcıyı login'e atma
    // console.error("verify hata ama token silinmedi:", e?.message, e?.response?.data);
    // istersen snackbar:
    // enqueueSnackbar("Sunucuya bağlanılamadı.", { variant: "warning" });
  }
} finally {
  setLoading(false);
}
    };

    verify();
  }, [token]);

  const handleLogin = async (kimlikTelefon, sifre) => {
    setLoading(true);

    const requestData = {
      KIMLIK_TELEFON: kimlikTelefon,
      SIFRE: sifre,
      FIREBASE_TOKEN_ID: "-",
      CIHAZ_TOKEN_ID: cihazTokenId,
    };

    try {
      const res = await apiLogin(requestData);
      const d = res?.data;

      if (d?.status !== true) {
        enqueueSnackbar(d?.message || "Giriş başarısız.", { variant: "error" });
        return;
      }

      if (d?.message === "SMSKOD") {
        enqueueSnackbar("SMS kodunu gir.", { variant: "info" });
        navigate("/sms-dogrulama", { state: { kimlikTelefon } });
        return;
      }

      const newToken = d?.data?.token;
      const userData = d?.data?.user || null;

      if (!newToken) {
        enqueueSnackbar("Token alınamadı.", { variant: "error" });
        return;
      }

      localStorage.setItem("token", newToken);
      setToken(newToken);
      setUser(userData);

      enqueueSnackbar("Giriş başarılı!", { variant: "success" });
      navigate("/uretim-donemi");
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.message || e?.message || "Giriş hatası", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async (showNotification = true) => {
    try {
      await apiLogout();
    } catch {
      // sorun değil
    }

    localStorage.removeItem("token");
    setToken(null);
    setUser(null);

    if (showNotification) {
      enqueueSnackbar("Çıkış yapıldı.", { variant: "info" });
    }

    navigate("/login");
  };

  const value = useMemo(
    () => ({
      token,
      user,
      loading,
      cihazTokenId,
      handleLogin,
      handleLogout,
    }),
    [token, user, loading, cihazTokenId]
  );

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};